// src/retryer.ts
import { focusManager } from "./focusManager.js";
import { onlineManager } from "./onlineManager.js";
import { pendingThenable } from "./thenable.js";
import { isServer, sleep } from "./utils.js";
function defaultRetryDelay(failureCount) {
  return Math.min(1e3 * 2 ** failureCount, 3e4);
}
function canFetch(networkMode) {
  return (networkMode !== null && networkMode !== void 0 ? networkMode : "online") === "online" ? onlineManager.isOnline() : true;
}
var CancelledError = class extends Error {
  constructor(options) {
    super("CancelledError");
    this.revert = options === null || options === void 0 ? void 0 : options.revert;
    this.silent = options === null || options === void 0 ? void 0 : options.silent;
  }
};
function isCancelledError(value) {
  return value instanceof CancelledError;
}
function createRetryer(config) {
  let isRetryCancelled = false;
  let failureCount = 0;
  let isResolved = false;
  let continueFn;
  const thenable = pendingThenable();
  const cancel = cancelOptions => {
    if (!isResolved) {
      var _config$abort;
      reject(new CancelledError(cancelOptions));
      (_config$abort = config.abort) === null || _config$abort === void 0 || _config$abort.call(config);
    }
  };
  const cancelRetry = () => {
    isRetryCancelled = true;
  };
  const continueRetry = () => {
    isRetryCancelled = false;
  };
  const canContinue = () => focusManager.isFocused() && (config.networkMode === "always" || onlineManager.isOnline()) && config.canRun();
  const canStart = () => canFetch(config.networkMode) && config.canRun();
  const resolve = value => {
    if (!isResolved) {
      var _config$onSuccess, _continueFn;
      isResolved = true;
      (_config$onSuccess = config.onSuccess) === null || _config$onSuccess === void 0 || _config$onSuccess.call(config, value);
      (_continueFn = continueFn) === null || _continueFn === void 0 || _continueFn();
      thenable.resolve(value);
    }
  };
  const reject = value => {
    if (!isResolved) {
      var _config$onError, _continueFn2;
      isResolved = true;
      (_config$onError = config.onError) === null || _config$onError === void 0 || _config$onError.call(config, value);
      (_continueFn2 = continueFn) === null || _continueFn2 === void 0 || _continueFn2();
      thenable.reject(value);
    }
  };
  const pause = () => {
    return new Promise(continueResolve => {
      var _config$onPause;
      continueFn = value => {
        if (isResolved || canContinue()) {
          continueResolve(value);
        }
      };
      (_config$onPause = config.onPause) === null || _config$onPause === void 0 || _config$onPause.call(config);
    }).then(() => {
      continueFn = void 0;
      if (!isResolved) {
        var _config$onContinue;
        (_config$onContinue = config.onContinue) === null || _config$onContinue === void 0 || _config$onContinue.call(config);
      }
    });
  };
  const run = () => {
    if (isResolved) {
      return;
    }
    let promiseOrValue;
    const initialPromise = failureCount === 0 ? config.initialPromise : void 0;
    try {
      promiseOrValue = initialPromise !== null && initialPromise !== void 0 ? initialPromise : config.fn();
    } catch (error) {
      promiseOrValue = Promise.reject(error);
    }
    Promise.resolve(promiseOrValue).then(resolve).catch(error => {
      var _config$retry, _config$retryDelay, _config$onFail;
      if (isResolved) {
        return;
      }
      const retry = (_config$retry = config.retry) !== null && _config$retry !== void 0 ? _config$retry : isServer ? 0 : 3;
      const retryDelay = (_config$retryDelay = config.retryDelay) !== null && _config$retryDelay !== void 0 ? _config$retryDelay : defaultRetryDelay;
      const delay = typeof retryDelay === "function" ? retryDelay(failureCount, error) : retryDelay;
      const shouldRetry = retry === true || typeof retry === "number" && failureCount < retry || typeof retry === "function" && retry(failureCount, error);
      if (isRetryCancelled || !shouldRetry) {
        reject(error);
        return;
      }
      failureCount++;
      (_config$onFail = config.onFail) === null || _config$onFail === void 0 || _config$onFail.call(config, failureCount, error);
      sleep(delay).then(() => {
        return canContinue() ? void 0 : pause();
      }).then(() => {
        if (isRetryCancelled) {
          reject(error);
        } else {
          run();
        }
      });
    });
  };
  return {
    promise: thenable,
    cancel,
    continue: () => {
      var _continueFn3;
      (_continueFn3 = continueFn) === null || _continueFn3 === void 0 || _continueFn3();
      return thenable;
    },
    cancelRetry,
    continueRetry,
    canStart,
    start: () => {
      if (canStart()) {
        run();
      } else {
        pause().then(run);
      }
      return thenable;
    }
  };
}
export { CancelledError, canFetch, createRetryer, isCancelledError };